import { useState, useEffect } from "react";
import { createPortal } from "react-dom";
import { useNavigate } from "react-router-dom";
import { Clock, Star, Users, Handshake, Briefcase, ClipboardList, Building2, List as ListIcon, MoreVertical, Search, X, CheckSquare, CalendarDays, Plus } from "lucide-react";
import { useTranslation } from "react-i18next";
import { Input } from "@/components/ui/input";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { useEntityHistory, HistoryEntry, EntityType } from "@/hooks/useEntityHistory";
import { useAuth } from "@/providers/AuthProvider";
import { supabase } from "@/integrations/supabase/client";
import { cn } from "@/lib/utils";
import { matchesCandidateSearch, matchesJobSearch, matchesCompanySearch, matchesListSearch, matchesTaskSearch, matchesEventSearch } from "@/utils/searchUtils";
import { AddCandidateSelectionModal } from "@/components/candidates/AddCandidateSelectionModal";
import { AddCandidateForm } from "@/components/candidates/AddCandidateForm";
import { AddCompanyForm } from "@/components/companies/AddCompanyForm";
import { JobOffersAddModal } from "@/components/jobs/JobOffersAddModal";
import { CreateTaskForm } from "@/components/tasks/CreateTaskForm";
import { CreateListForm } from "@/components/lists/CreateListForm";
import { CreateEventModal } from "@/components/calendar/CreateEventModal";
import { AddWorkforceModal } from "@/components/workforce/AddWorkforceModal";
import { CandidateFormData } from "@/utils/candidate";
import { Candidate } from "@/types/candidate";
import { useAddCompany } from "@/hooks/useAddCompany";
import { useAddJob } from "@/hooks/jobs/useAddJob";
import { useCreateList } from "@/hooks/lists/useLists";

interface FavoriteEntry {
	id: string;
	entity_type: EntityType;
	entity_id: string;
	entity_name: string;
	metadata?: Record<string, any>;
	created_at: string;
}

interface RecentHistoryPopoverProps {
	entityType: EntityType;
}

const getEntityIcon = (entityType: string) => {
	switch (entityType) {
		case 'candidate':
			return Users;
		case 'job':
			return ClipboardList;
		case 'company':
			return Building2;
		case 'list':
			return ListIcon;
		case 'match':
			return Briefcase;
		case 'task':
			return CheckSquare;
		case 'query':
			return Search;
		case 'event':
			return CalendarDays;
		case 'workforce':
			return Handshake;
		default:
			return Clock;
	}
};

const getEntityPath = (entityType: string, entityId: string) => {
	switch (entityType) {
		case 'candidate':
			return `/candidates/${entityId}`;
		case 'job':
			return `/jobs/${entityId}`;
		case 'company':
			return `/companies/${entityId}`;
		case 'list':
			return `/lists/${entityId}`;
		case 'match':
			return `/matches/${entityId}`;
		case 'task':
			return `/tasks?task=${entityId}`;
		case 'query':
			return `/queries/${entityId}`;
		case 'event':
			return `/calendar?event=${entityId}`;
		case 'workforce':
			return `/workforce/${entityId}`;
		default:
			return '/';
	}
};

export const RecentHistoryPopover = ({ entityType }: RecentHistoryPopoverProps) => {
	const { t } = useTranslation();
	const navigate = useNavigate();
	const { user } = useAuth();
	const { getRecentHistory } = useEntityHistory();

	// Determine if this entity type supports favorites
	const hasFavorites = entityType !== 'match' && entityType !== 'task' && entityType !== 'event';

	const [history, setHistory] = useState<HistoryEntry[]>([]);
	const [favorites, setFavorites] = useState<FavoriteEntry[]>([]);
	const [isOpen, setIsOpen] = useState(false);

	// Add entity dialog state
	const { addCompany } = useAddCompany();
	const { addJob } = useAddJob();
	const createListMutation = useCreateList();
	const [isAddCandidateSelectionOpen, setIsAddCandidateSelectionOpen] = useState(false);
	const [isAddCandidateFormOpen, setIsAddCandidateFormOpen] = useState(false);
	const [candidatePrefillData, setCandidatePrefillData] = useState<CandidateFormData | null>(null);
	const [isAddCompanyOpen, setIsAddCompanyOpen] = useState(false);
	const [isAddJobOpen, setIsAddJobOpen] = useState(false);
	const [isAddTaskOpen, setIsAddTaskOpen] = useState(false);
	const [isAddListOpen, setIsAddListOpen] = useState(false);
	const [isAddEventOpen, setIsAddEventOpen] = useState(false);
	const [isAddWorkforceOpen, setIsAddWorkforceOpen] = useState(false);

	const canCreate = entityType !== 'match' && entityType !== 'query';

	const handleCreateNew = () => {
		setIsOpen(false);
		if (entityType === 'candidate') setIsAddCandidateSelectionOpen(true);
		else if (entityType === 'company') setIsAddCompanyOpen(true);
		else if (entityType === 'job') setIsAddJobOpen(true);
		else if (entityType === 'task') setIsAddTaskOpen(true);
		else if (entityType === 'list') setIsAddListOpen(true);
		else if (entityType === 'event') setIsAddEventOpen(true);
		else if (entityType === 'workforce') setIsAddWorkforceOpen(true);
	};

	const handleAddCandidateManual = () => {
		setIsAddCandidateSelectionOpen(false);
		setCandidatePrefillData(null);
		setIsAddCandidateFormOpen(true);
	};

	const handleAddCandidateCVUpload = (formData: CandidateFormData) => {
		setIsAddCandidateSelectionOpen(false);
		setCandidatePrefillData(formData);
		setIsAddCandidateFormOpen(true);
	};

	const handleCandidateAdded = (_newCandidate: Candidate) => {
		setIsAddCandidateFormOpen(false);
		setCandidatePrefillData(null);
	};

	const handleCompanySubmit = async (companyData: any) => {
		try {
			const success = await addCompany(companyData);
			if (success) {
				setIsAddCompanyOpen(false);
				return true;
			}
			return false;
		} catch {
			return false;
		}
	};

	const handleCreateJob = async (jobData: any) => {
		try {
			const jobId = await addJob(jobData);
			if (jobId) {
				setIsAddJobOpen(false);
				return jobId;
			}
			return undefined;
		} catch {
			return undefined;
		}
	};

	const handleCreateList = async (name: string, description?: string) => {
		await createListMutation.mutateAsync({ name, description });
		setIsAddListOpen(false);
	};
	const [isLoading, setIsLoading] = useState(false);
	const [favoritesSearchQuery, setFavoritesSearchQuery] = useState("");
	const [entitySearchQuery, setEntitySearchQuery] = useState("");
	const [favoriteSearchResults, setFavoriteSearchResults] = useState<FavoriteEntry[]>([]);
	const [entitySearchResults, setEntitySearchResults] = useState<any[]>([]);
	const [isSearching, setIsSearching] = useState(false);

	const loadData = async () => {
		setIsLoading(true);
		try {
			// Load history filtered by entity type
			const recentHistory = await getRecentHistory();
			const filteredHistory = recentHistory
				.filter(entry => entry.entity_type === entityType)
				.slice(0, 20); // Get top 20
			setHistory(filteredHistory);

			// Load favorites with entity details using RPC
			if (user) {
				const { data: favData, error } = await supabase
					.rpc('get_user_favorites_with_details', {
						p_user_id: user.id,
						p_entity_type: entityType,
						p_limit: 20
					});

				if (!error && favData) {
					let favoritesData = favData as FavoriteEntry[];
					
					// For queries, the RPC might not populate entity_name, so fetch them separately
					if (entityType === 'query') {
						const queryIds = favoritesData
							.filter(fav => !fav.entity_name)
							.map(fav => fav.entity_id);

						if (queryIds.length > 0) {
							const { data: queryData, error: queryError } = await supabase
								.from('saved_queries')
								.select('id, name, description')
								.in('id', queryIds);

							if (!queryError && queryData) {
								const queryMap = new Map(queryData.map(q => [q.id, { name: q.name, description: q.description }]));

								favoritesData = favoritesData.map(fav => {
									if (!fav.entity_name && queryMap.has(fav.entity_id)) {
										const queryInfo = queryMap.get(fav.entity_id)!;
										return {
											...fav,
											entity_name: queryInfo.name,
											metadata: {
												...fav.metadata,
												description: queryInfo.description
											}
										};
									}
									return fav;
								});
							}
						}
					}

					// For workforce, the RPC doesn't populate entity_name, so fetch from workforce + candidates
					if (entityType === 'workforce') {
						const workforceIds = favoritesData
							.filter(fav => !fav.entity_name)
							.map(fav => fav.entity_id);

						if (workforceIds.length > 0) {
							const { data: wfData, error: wfError } = await supabase
								.from('workforce')
								.select('id, candidates!inner(name), jobs(title, companies(name)), external_company_name, external_job_title')
								.in('id', workforceIds);

							if (!wfError && wfData) {
								const wfMap = new Map(wfData.map((w: any) => [w.id, {
									name: w.candidates?.name,
									companyName: w.jobs?.companies?.name || w.external_company_name,
									jobTitle: w.jobs?.title || w.external_job_title
								}]));

								favoritesData = favoritesData.map(fav => {
									if (!fav.entity_name && wfMap.has(fav.entity_id)) {
										const wfInfo = wfMap.get(fav.entity_id)!;
										return {
											...fav,
											entity_name: wfInfo.name || '',
											metadata: {
												...fav.metadata,
												companyName: wfInfo.companyName,
												jobTitle: wfInfo.jobTitle
											}
										};
									}
									return fav;
								});
							}
						}
					}

					setFavorites(favoritesData);
				}
			}
		} catch (error) {
			console.error('Error loading history/favorites:', error);
		} finally {
			setIsLoading(false);
		}
	};

	const handleEntityClick = (entityType: string, entityId: string) => {
		const path = getEntityPath(entityType, entityId);
		navigate(path);
		setIsOpen(false);
	};

	const searchEntities = async (query: string) => {
		if (!query.trim()) {
			setEntitySearchResults([]);
			return;
		}

		setIsSearching(true);
		try {
			let results: any[] = [];

			switch (entityType) {
				case 'candidate': {
					const { data } = await supabase
						.from('candidates')
						.select('id, name, email, function, skills');
					
					if (data) {
						const matched = data
							.filter(candidate => matchesCandidateSearch(candidate, query))
							.slice(0, 20);
						results = matched.map(c => ({ 
							id: c.id, 
							name: c.name, 
							metadata: { function: c.function } 
						}));
					}
					break;
				}
				case 'job': {
					const { data } = await supabase
						.from('jobs')
						.select('id, title, description, skills, company:company_id(name)');
					
					if (data) {
						const matched = data
							.filter(job => matchesJobSearch({
								...job,
								company: Array.isArray(job.company) ? job.company[0] : job.company,
								skills: job.skills || [],
								hardSkills: [],
								softSkills: [],
								employmentType: null,
								timeCommitment: null,
								seniorityLevel: null
							}, query))
							.slice(0, 20);
						results = matched.map(j => ({ 
							id: j.id, 
							name: j.title, 
							metadata: { 
								companyName: Array.isArray(j.company) ? j.company[0]?.name : (j.company as { name: string } | null)?.name
							} 
						}));
					}
					break;
				}
				case 'company': {
					const { data } = await supabase
						.from('companies')
						.select('id, name, city, country, assignee');
					
					if (data) {
						const matched = data
							.filter(company => matchesCompanySearch({
								...company,
								street: null,
								vat_number: null,
								website_url: null,
								notes: null
							}, query))
							.slice(0, 20);
						results = matched.map(c => ({ 
							id: c.id, 
							name: c.name, 
							metadata: { city: c.city } 
						}));
					}
					break;
				}
				case 'list': {
					const { data } = await supabase
						.from('lists_with_candidate_count')
						.select('id, name, description, created_by, custom_fields, candidate_count');
					
					if (data) {
						const matched = data
							.filter(list => matchesListSearch(list, query))
							.slice(0, 20);
						results = matched.map(l => ({ 
							id: l.id, 
							name: l.name, 
							metadata: { candidate_count: l.candidate_count } 
						}));
					}
					break;
				}
				case 'task': {
					const { data } = await supabase
						.from('tasks')
						.select('id, name, description, status, priority');

					if (data) {
						const matched = data
							.filter(task => matchesTaskSearch(task, query))
							.slice(0, 20);
						results = matched.map(t => ({
							id: t.id,
							name: t.name,
							metadata: { status: t.status, priority: t.priority }
						}));
					}
					break;
				}
				case 'event': {
					const { data } = await supabase
						.from('events')
						.select('id, title, description, location, status, priority');

					if (data) {
						const matched = data
							.filter(event => matchesEventSearch(event, query))
							.slice(0, 20);
						results = matched.map(e => ({
							id: e.id,
							name: e.title,
							metadata: { status: e.status, location: e.location }
						}));
					}
					break;
				}
				case 'query': {
					const { data } = await supabase
						.from('saved_queries')
						.select('id, name, description');

					if (data) {
						const lowerQuery = query.toLowerCase();
						const matched = data
							.filter(q =>
								q.name.toLowerCase().includes(lowerQuery) ||
								(q.description && q.description.toLowerCase().includes(lowerQuery))
							)
							.slice(0, 20);
						results = matched.map(q => ({
							id: q.id,
							name: q.name,
							metadata: { description: q.description }
						}));
					}
					break;
				}
				case 'workforce': {
					const { data } = await supabase
						.from('workforce')
						.select('id, candidates!inner(name), jobs(title, companies(name)), external_company_name, external_job_title');

					if (data) {
						const lowerQuery = query.toLowerCase();
						const matched = data
							.filter((w: any) => {
								const candidateName = w.candidates?.name?.toLowerCase() || '';
								const companyName = w.jobs?.companies?.name?.toLowerCase() || w.external_company_name?.toLowerCase() || '';
								const jobTitle = w.jobs?.title?.toLowerCase() || w.external_job_title?.toLowerCase() || '';
								return candidateName.includes(lowerQuery) ||
									   companyName.includes(lowerQuery) ||
									   jobTitle.includes(lowerQuery);
							})
							.slice(0, 20);
						results = matched.map((w: any) => ({
							id: w.id,
							name: w.candidates?.name || 'Unknown',
							metadata: {
								companyName: w.jobs?.companies?.name || w.external_company_name,
								jobTitle: w.jobs?.title || w.external_job_title
							}
						}));
					}
					break;
				}
			}

			setEntitySearchResults(results);
		} catch (error) {
			console.error('Error searching entities:', error);
		} finally {
			setIsSearching(false);
		}
	};

	const searchFavorites = async (query: string) => {
		if (!query.trim()) {
			setFavoriteSearchResults([]);
			return;
		}

		setIsSearching(true);
		try {
			if (!user) return;
			
			const { data: favData, error } = await supabase
				.rpc('get_user_favorites_with_details', {
					p_user_id: user.id,
					p_entity_type: entityType,
					p_limit: 100
				});

			if (error) throw error;
			
			if (favData) {
				let favoritesData = favData as FavoriteEntry[];
				
				// For queries, the RPC might not populate entity_name, so fetch them separately
				if (entityType === 'query') {
					const queryIds = favoritesData
						.filter(fav => !fav.entity_name)
						.map(fav => fav.entity_id);

					if (queryIds.length > 0) {
						const { data: queryData, error: queryError } = await supabase
							.from('saved_queries')
							.select('id, name, description')
							.in('id', queryIds);

						if (!queryError && queryData) {
							const queryMap = new Map(queryData.map(q => [q.id, { name: q.name, description: q.description }]));

							favoritesData = favoritesData.map(fav => {
								if (!fav.entity_name && queryMap.has(fav.entity_id)) {
									const queryInfo = queryMap.get(fav.entity_id)!;
									return {
										...fav,
										entity_name: queryInfo.name,
										metadata: {
											...fav.metadata,
											description: queryInfo.description
										}
									};
								}
								return fav;
							});
						}
					}
				}

				// For workforce, fetch candidate/job details
				if (entityType === 'workforce') {
					const workforceIds = favoritesData
						.filter(fav => !fav.entity_name)
						.map(fav => fav.entity_id);

					if (workforceIds.length > 0) {
						const { data: wfData, error: wfError } = await supabase
							.from('workforce')
							.select('id, candidates!inner(name), jobs(title, companies(name)), external_company_name, external_job_title')
							.in('id', workforceIds);

						if (!wfError && wfData) {
							const wfMap = new Map(wfData.map((w: any) => [w.id, {
								name: w.candidates?.name,
								companyName: w.jobs?.companies?.name || w.external_company_name,
								jobTitle: w.jobs?.title || w.external_job_title
							}]));

							favoritesData = favoritesData.map(fav => {
								if (!fav.entity_name && wfMap.has(fav.entity_id)) {
									const wfInfo = wfMap.get(fav.entity_id)!;
									return {
										...fav,
										entity_name: wfInfo.name || '',
										metadata: {
											...fav.metadata,
											companyName: wfInfo.companyName,
											jobTitle: wfInfo.jobTitle
										}
									};
								}
								return fav;
							});
						}
					}
				}

				const lowerQuery = query.toLowerCase();
				const results = favoritesData.filter(fav => {
					if (fav.entity_name.toLowerCase().includes(lowerQuery)) return true;
					
					if (fav.metadata) {
						const metadataValues = Object.values(fav.metadata)
							.filter(val => typeof val === 'string' || typeof val === 'number')
							.map(val => String(val).toLowerCase());
						
						if (metadataValues.some(val => val.includes(lowerQuery))) return true;
					}
					
					return false;
				}).slice(0, 20);
				
				setFavoriteSearchResults(results);
			}
		} catch (error) {
			console.error('Error searching favorites:', error);
		} finally {
			setIsSearching(false);
		}
	};

	// useEffect hooks
	useEffect(() => {
		if (isOpen && user) {
			loadData();
		}
	}, [isOpen, user]);

	// Debounced entity search
	useEffect(() => {
		const timer = setTimeout(() => {
			if (entitySearchQuery.trim()) {
				searchEntities(entitySearchQuery);
			} else {
				setEntitySearchResults([]);
			}
		}, 300);

		return () => clearTimeout(timer);
	}, [entitySearchQuery]);

	// Debounced favorites search
	useEffect(() => {
		const timer = setTimeout(() => {
			if (favoritesSearchQuery.trim()) {
				searchFavorites(favoritesSearchQuery);
			} else {
				setFavoriteSearchResults([]);
			}
		}, 300);

		return () => clearTimeout(timer);
	}, [favoritesSearchQuery]);

	const renderHistoryItem = (entry: HistoryEntry) => {
		const Icon = getEntityIcon(entry.entity_type);
		
		return (
			<button
				key={entry.id}
				onClick={() => handleEntityClick(entry.entity_type, entry.entity_id)}
				className="w-full text-left px-3 py-2 hover:bg-accent rounded-sm flex items-center gap-3 transition-colors group"
			>
				<div className="flex items-center justify-center w-8 h-8 rounded-sm bg-muted group-hover:bg-muted/80">
					<Icon className="h-4 w-4 text-muted-foreground" />
				</div>
				<div className="flex-1 min-w-0">
					<p className="text-sm font-medium truncate">
						{entry.entity_name || t(`navigation.history.entityType.${entry.entity_type}`, entry.entity_type)}
					</p>
					<p className="text-xs text-muted-foreground">
						{t(`navigation.history.entityType.${entry.entity_type}`, entry.entity_type)}
						{entry.metadata?.companyName && ` • ${entry.metadata.companyName}`}
						{entry.metadata?.candidateFunction && ` • ${entry.metadata.candidateFunction}`}
						{entry.metadata?.jobTitle && ` • ${entry.metadata.jobTitle}`}
						{entry.metadata?.description && ` • ${entry.metadata.description}`}
					</p>
				</div>
			</button>
		);
	};

	const renderFavoriteItem = (fav: FavoriteEntry) => {
		const Icon = getEntityIcon(fav.entity_type);
		
		return (
			<button
				key={fav.id}
				onClick={() => handleEntityClick(fav.entity_type, fav.entity_id)}
				className="w-full text-left px-3 py-2 hover:bg-accent rounded-sm flex items-center gap-3 transition-colors group"
			>
				<div className="flex items-center justify-center w-8 h-8 rounded-sm bg-yellow-50 dark:bg-yellow-900/20 group-hover:bg-yellow-100 dark:group-hover:bg-yellow-900/30">
					<Icon className="h-4 w-4 text-yellow-600 dark:text-yellow-500" />
				</div>
				<div className="flex-1 min-w-0">
					<div className="flex items-center gap-1.5">
						<Star className="h-3 w-3 text-yellow-500 fill-current flex-shrink-0" />
						<p className="text-sm font-medium truncate">{fav.entity_name}</p>
					</div>
					<p className="text-xs text-muted-foreground">
						{t(`navigation.history.entityType.${fav.entity_type}`, fav.entity_type)}
						{fav.metadata?.function && ` • ${fav.metadata.function}`}
						{fav.metadata?.companyName && ` • ${fav.metadata.companyName}`}
						{fav.metadata?.jobTitle && ` • ${fav.metadata.jobTitle}`}
						{fav.metadata?.city && ` • ${fav.metadata.city}`}
						{fav.metadata?.candidate_count !== undefined && ` • ${fav.metadata.candidate_count} candidates`}
						{fav.metadata?.status && ` • ${fav.metadata.status}`}
						{fav.metadata?.description && ` • ${fav.metadata.description}`}
					</p>
				</div>
			</button>
		);
	};

	return (
		<>
		<Popover open={isOpen} onOpenChange={setIsOpen}>
			<PopoverTrigger asChild>
				<button
					className="opacity-0 group-hover:opacity-100 transition-opacity p-0.5 hover:bg-accent rounded-sm"
					title={t('navigation.recentHistory', 'Recent & Favorites')}
					onClick={(e) => {
						e.preventDefault();
						e.stopPropagation();
						setIsOpen(!isOpen);
					}}
				>
					<MoreVertical className="h-4 w-4" />
				</button>
			</PopoverTrigger>
			<PopoverContent
				className="w-[420px] p-0"
				align="start"
				side="right"
				sideOffset={4}
				avoidCollisions={true}
				collisionPadding={{ top: 80, bottom: 20, left: 20, right: 20 }}
			>
				<Tabs defaultValue="history" className="w-full">
					<TabsList className="w-full rounded-none border-0 border-b border-border">
						<TabsTrigger value="history" className="flex-1 gap-2">
							<Clock className="h-4 w-4" />
							{t('navigation.history.title', 'Recent')}
						</TabsTrigger>
						{hasFavorites && (
							<TabsTrigger value="favorites" className="flex-1 gap-2">
								<Star className="h-4 w-4" />
								{t('navigation.favorites.title', 'Favorites')}
							</TabsTrigger>
						)}
						<TabsTrigger value="search" className="flex-1 gap-2">
							<Search className="h-4 w-4" />
							{t('navigation.search.title', 'Search')}
						</TabsTrigger>
						{canCreate && (
							<button
								type="button"
								onClick={handleCreateNew}
								className="flex items-center gap-1 px-2 py-1 text-xs text-blue-600 bg-blue-100 hover:bg-blue-200 rounded-sm outline-none border-0 transition-colors shrink-0"
							>
								<Plus className="h-3 w-3" />
								{t('common.new', 'New')}
							</button>
						)}
					</TabsList>

					<TabsContent value="history" className="p-2 max-h-[450px] overflow-y-auto mt-0">
						{isLoading ? (
							<div className="flex items-center justify-center py-8">
								<p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
							</div>
						) : history.length === 0 ? (
							<div className="flex flex-col items-center justify-center py-8 text-center">
								<Clock className="h-8 w-8 text-muted-foreground mb-2" />
								<p className="text-sm text-muted-foreground">{t('navigation.history.empty', 'No recent items')}</p>
							</div>
						) : (
							<div className="space-y-1">
								{history.map(renderHistoryItem)}
							</div>
						)}
					</TabsContent>
					
					{hasFavorites && (
						<TabsContent value="favorites" className="max-h-[450px] overflow-y-auto">
							<div className="p-3 pb-2 sticky top-0 bg-background z-10">
							<div className="relative">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
								<Input
									type="text"
									placeholder={t('common.search', 'Search...')}
									value={favoritesSearchQuery}
									onChange={(e) => setFavoritesSearchQuery(e.target.value)}
									className="pl-9 pr-9 h-9"
								/>
								{favoritesSearchQuery && (
									<button
										onClick={() => {
											setFavoritesSearchQuery('');
											setFavoriteSearchResults([]);
										}}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
									>
										<X className="h-4 w-4" />
									</button>
								)}
							</div>
						</div>
						<div className="p-2 pt-0">
							{isLoading ? (
								<div className="flex items-center justify-center py-8">
									<p className="text-sm text-muted-foreground">{t('common.loading', 'Loading...')}</p>
								</div>
							) : favoritesSearchQuery ? (
								isSearching ? (
									<div className="flex items-center justify-center py-8">
										<p className="text-sm text-muted-foreground">{t('common.searching', 'Searching...')}</p>
									</div>
								) : favoriteSearchResults.length === 0 ? (
									<div className="flex flex-col items-center justify-center py-8 text-center">
										<Star className="h-8 w-8 text-muted-foreground mb-2" />
										<p className="text-sm text-muted-foreground">{t('common.noResults', 'No results found')}</p>
									</div>
								) : (
									<div className="space-y-1">
										{favoriteSearchResults.map(renderFavoriteItem)}
									</div>
								)
							) : favorites.length === 0 ? (
								<div className="flex flex-col items-center justify-center py-8 text-center">
									<Star className="h-8 w-8 text-muted-foreground mb-2" />
									<p className="text-sm text-muted-foreground">{t('navigation.favorites.empty', 'No favorites yet')}</p>
									<p className="text-xs text-muted-foreground mt-1">
										{t('navigation.favorites.emptyHint', 'Click the star icon on items to add them as favorites')}
									</p>
								</div>
							) : (
								<div className="space-y-1">
									{favorites.map(renderFavoriteItem)}
								</div>
							)}
						</div>
					</TabsContent>
					)}
					
					<TabsContent value="search" className="max-h-[450px] overflow-y-auto">
						<div className="p-3 pb-2 sticky top-0 bg-background z-10">
							<div className="relative">
								<Search className="absolute left-3 top-1/2 -translate-y-1/2 h-4 w-4 text-muted-foreground" />
								<Input
									type="text"
									placeholder={t('common.search', 'Search...')}
									value={entitySearchQuery}
									onChange={(e) => setEntitySearchQuery(e.target.value)}
									className="pl-9 pr-9 h-9"
								/>
								{entitySearchQuery && (
									<button
										onClick={() => {
											setEntitySearchQuery('');
											setEntitySearchResults([]);
										}}
										className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground"
									>
										<X className="h-4 w-4" />
									</button>
								)}
							</div>
						</div>
						<div className="p-2 pt-0">
							{!entitySearchQuery ? (
								<div className="flex flex-col items-center justify-center py-8 text-center">
									<Search className="h-8 w-8 text-muted-foreground mb-2" />
									<p className="text-sm text-muted-foreground">{t('navigation.search.empty', 'Type to search...')}</p>
								</div>
							) : isSearching ? (
								<div className="flex items-center justify-center py-8">
									<p className="text-sm text-muted-foreground">{t('common.searching', 'Searching...')}</p>
								</div>
							) : entitySearchResults.length === 0 ? (
								<div className="flex flex-col items-center justify-center py-8 text-center">
									<Search className="h-8 w-8 text-muted-foreground mb-2" />
									<p className="text-sm text-muted-foreground">{t('common.noResults', 'No results found')}</p>
								</div>
							) : (
								<div className="space-y-1">
									{entitySearchResults.map(result => {
										const Icon = getEntityIcon(entityType);
										return (
											<button
												key={result.id}
												onClick={() => handleEntityClick(entityType, result.id)}
												className="w-full text-left px-3 py-2 hover:bg-accent rounded-sm flex items-center gap-3 transition-colors group"
											>
												<div className="flex items-center justify-center w-8 h-8 rounded-sm bg-muted group-hover:bg-muted/80">
													<Icon className="h-4 w-4 text-muted-foreground" />
												</div>
												<div className="flex-1 min-w-0">
													<p className="text-sm font-medium truncate">{result.name}</p>
													<p className="text-xs text-muted-foreground">
														{t(`navigation.history.entityType.${entityType}`, entityType)}
														{result.metadata?.function && ` • ${result.metadata.function}`}
														{result.metadata?.companyName && ` • ${result.metadata.companyName}`}
														{result.metadata?.jobTitle && ` • ${result.metadata.jobTitle}`}
														{result.metadata?.city && ` • ${result.metadata.city}`}
														{result.metadata?.candidate_count !== undefined && ` • ${result.metadata.candidate_count} candidates`}
														{result.metadata?.status && ` • ${result.metadata.status}`}
														{result.metadata?.description && ` • ${result.metadata.description}`}
													</p>
												</div>
											</button>
										);
									})}
								</div>
							)}
						</div>
					</TabsContent>
				</Tabs>
			</PopoverContent>
		</Popover>
		{/* These use ModalForm (Radix portal) — safe to render anywhere */}
		<AddCandidateForm
			isOpen={isAddCandidateFormOpen}
			onClose={() => { setIsAddCandidateFormOpen(false); setCandidatePrefillData(null); }}
			onCandidateAdded={handleCandidateAdded}
			initialFormData={candidatePrefillData}
		/>
		<AddCompanyForm
			isOpen={isAddCompanyOpen}
			onClose={() => setIsAddCompanyOpen(false)}
			onSubmit={handleCompanySubmit}
		/>
		<JobOffersAddModal
			isOpen={isAddJobOpen}
			onClose={() => setIsAddJobOpen(false)}
			onAddJob={handleCreateJob}
		/>
		<CreateListForm
			isOpen={isAddListOpen}
			onClose={() => setIsAddListOpen(false)}
			onSubmit={handleCreateList}
			isLoading={createListMutation.isPending}
		/>
		{/* These use fixed divs without a portal — must be portalled to document.body */}
		{createPortal(
			<AddCandidateSelectionModal
				isOpen={isAddCandidateSelectionOpen}
				onClose={() => setIsAddCandidateSelectionOpen(false)}
				onManualCreate={handleAddCandidateManual}
				onCVUpload={handleAddCandidateCVUpload}
			/>,
			document.body
		)}
		{createPortal(
			<CreateTaskForm
				isOpen={isAddTaskOpen}
				onClose={() => setIsAddTaskOpen(false)}
			/>,
			document.body
		)}
		{createPortal(
			<CreateEventModal
				isOpen={isAddEventOpen}
				onClose={() => setIsAddEventOpen(false)}
			/>,
			document.body
		)}
		{createPortal(
			<AddWorkforceModal
				open={isAddWorkforceOpen}
				onOpenChange={setIsAddWorkforceOpen}
			/>,
			document.body
		)}
		</>
	);
};
